// Divergência 3 Fatia B — aba "Mensagens" do CaseDetailPage (comitê → denunciante).
//
// O comitê LÊ o thread do canal (comite_message + denunciante_message) e ENVIA uma
// mensagem cifrada sob a DEK do caso (AAD=caseId+ulid, messageAad). Toda a cripto
// vive em case-crypto (encryptCaseMessage / decryptCaseMessages) — o DEK só existe
// dentro de withCaseDek e é zerado lá. Nada de texto claro sai do browser: o POST
// leva só {ulid, ciphertext, nonce} base64.
//
// GATES (dois, distintos):
//  - COMITE_MESSAGING_ENABLED (feature): a ABA só aparece quando o worker projeta
//    capabilities.messagingEnabled=true. Componente inerte com a flag OFF (nem monta).
//  - Acesso à DEK (COMITE_READ, via member-decrypt): caseCrypto.status!=='ok' →
//    envio INDISPONÍVEL (sem a DEK não há como cifrar/decifrar). É o acoplamento de
//    go-live: enviar de verdade exige a leitura do comitê live.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../contexts/SessionContext';
import { Alert, Button, Textarea, dataHora } from '@aions/ui-primitives';
import { apiFetch, logout } from '../lib/auth-client';
import {
  encryptCaseMessage,
  decryptCaseMessages,
  withCaseDek,
  type CaseDekAccess,
  type CaseMessageInput,
  type DecryptedCaseMessage,
} from '../lib/case-crypto';
import { kekMortaNestaSessao } from '../lib/kek-member-session';
import { baixarAnexo, salvarComo, type AnexoDownloadErro } from '../lib/anexo-download';

interface Props {
  caseId: string;
  /** Anexos do caso (`attachmentRefs` da projeção). Renderizados no thread com botão de
   *  download — o operador pediu que os arquivos do denunciante fiquem acessíveis aqui.
   *
   *  OPCIONAL: projeção que pode não estar no ar. Ausente ⇒ a seção não aparece e o
   *  thread fica idêntico ao que era, em vez de a aba quebrar. */
  attachments?: ReadonlyArray<{ contentHash: string; nonce: string; sizeBucket: string }>;
  caseCrypto: CaseDekAccess;
  /** Thread cru do GET /cases/:id (todas as origens; filtramos p/ o canal Div3). */
  messages: ReadonlyArray<CaseMessageInput>;
  /** Dispara o reload do caso no parent após um envio bem-sucedido. */
  onSent: () => void;
}

/** Só as duas origens do canal bidirecional Div3 aparecem nesta aba. */
const CANAL_DIV3 = new Set(['comite_message', 'denunciante_message']);

type SendState = 'idle' | 'sending' | 'sent' | 'error';

export function MessageComposer({
  caseId,
  caseCrypto,
  messages,
  attachments,
  onSent,
}: Props): JSX.Element {
  /** Hash do anexo em download, para desabilitar o botão e mostrar progresso. */
  const [baixando, setBaixando] = useState<string | null>(null);
  /** Erro do último download, por hash. */
  const [erroAnexo, setErroAnexo] = useState<{ hash: string; erro: AnexoDownloadErro } | null>(
    null,
  );

  /**
   * Baixa e decifra UM anexo.
   *
   * A DEK é aberta por `withCaseDek` — a mesma via do teor e do thread. Sem acesso
   * (COMITE_READ off, KEK morta) o botão nem aparece, então aqui o acesso já é `ok`.
   */
  const handleBaixar = async (contentHash: string): Promise<void> => {
    if (caseCrypto.status !== 'ok') return;
    setBaixando(contentHash);
    setErroAnexo(null);
    try {
      const r = await withCaseDek(caseCrypto, async (dek) => baixarAnexo(caseId, contentHash, dek));
      if (r.ok) {
        salvarComo(r.valor.bytes, r.valor.nomeSugerido);
      } else {
        setErroAnexo({ hash: contentHash, erro: r.erro });
      }
    } catch {
      setErroAnexo({ hash: contentHash, erro: 'falha_decrypt' });
    } finally {
      setBaixando(null);
    }
  };

  const { t } = useTranslation();
  const { setSession } = useSession();
  const [texto, setTexto] = useState('');
  const [sendState, setSendState] = useState<SendState>('idle');
  const [thread, setThread] = useState<DecryptedCaseMessage[]>([]);

  const canalMessages = messages.filter((m) => m.origin !== undefined && CANAL_DIV3.has(m.origin));
  const temAcesso = caseCrypto.status === 'ok';
  // Regra 1 — sonda da KEK no MOUNT. Deps: [temAcesso] (booleano derivado), NÃO
  // caseCrypto: o objeto é recriado a cada render do pai e sondaria sem motivo.
  const [sessaoExpirada, setSessaoExpirada] = useState(false);
  useEffect(() => {
    if (!temAcesso) return;
    let cancelled = false;
    void (async () => {
      // Sem catch local: `kekMortaNestaSessao` NUNCA lança e só devolve true com
      // determinação positiva (ver o contrato na lib — aviso antecipado, não gate).
      const morta = await kekMortaNestaSessao();
      if (!cancelled && morta) setSessaoExpirada(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [temAcesso]);

  // Decifra o thread numa única abertura da DEK sempre que a lista muda. Sem acesso
  // (COMITE_READ OFF) → thread vazio (a UI mostra "indisponível" no lugar do composer).
  useEffect(() => {
    let cancelled = false;
    if (caseCrypto.status !== 'ok') {
      setThread([]);
      return;
    }
    void (async () => {
      try {
        const dec = await decryptCaseMessages(caseCrypto, caseId, canalMessages);
        if (!cancelled) setThread(dec);
      } catch {
        if (!cancelled) setThread([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Recomputa quando o caso, o acesso à DEK, ou os ulids do canal mudam.
  }, [caseId, caseCrypto.status, canalMessages.map((m) => m.ulid).join(',')]);

  const handleEnviar = async (): Promise<void> => {
    if (caseCrypto.status !== 'ok' || texto.trim() === '') return;
    setSendState('sending');
    try {
      // ulid gerado no browser ANTES de cifrar — amarra a AAD (messageAad) e viaja
      // no POST para o denunciante recompor a MESMA AAD. Opaco (sem timestamp/PII).
      const ulid = crypto.randomUUID();
      const enc = await encryptCaseMessage(caseCrypto, caseId, ulid, texto.trim());
      const resp = await apiFetch(`/api/comite/cases/${caseId}/mensagem`, {
        method: 'POST',
        body: JSON.stringify({ ulid, ciphertext: enc.ciphertext, nonce: enc.nonce }),
      });
      if (!resp.ok) {
        setSendState('error');
        return;
      }
      setTexto('');
      setSendState('sent');
      onSent();
    } catch {
      setSendState('error');
    }
  };

  return (
    <section className="review-section" aria-label={t('mensagens.aria_label')}>
      {sessaoExpirada && (
        <Alert
          variant="warning"
          role="status"
          aria-live="polite"
          title={t('case_detail.sessao_expirada_title')}
          style={{ marginBottom: 12 }}
        >
          <div>{t('case_detail.sessao_expirada_body')}</div>
          {/* Incondicional, e é DECLARAÇÃO DE CONSEQUÊNCIA, não instrução: "copie o
              texto" assumiria que existe texto — condição que pode errar (a sonda é
              assíncrona e o campo fica ativo até ela responder). "Descarta qualquer
              texto não enviado" é verdade com ou sem rascunho. */}
          <div style={{ marginTop: 6 }}>{t('case_detail.sessao_expirada_descarte')}</div>
          <div style={{ marginTop: 10 }}>
            <Button
              variant="primary"
              size="sm"
              data-testid="sessao-expirada-entrar"
              onClick={() => {
                void (async () => {
                  await logout();
                  setSession(null);
                })();
              }}
            >
              {t('case_detail.sessao_expirada_acao')}
            </Button>
          </div>
        </Alert>
      )}
      {/* O <Alert> âmbar que vivia aqui foi ABSORVIDO pela margem da caixa (ver
          `.msgbox__rail` no CSS). Aviso em bloco solto se lê uma vez e vira
          paisagem; esta regra restringe cada frase digitada, então passou a ser
          a lombada da própria caixa — impossível de dispensar sem dispensar a
          caixa. Não foi remoção de conteúdo: o texto continua, como prosa, na
          ordem de leitura. */}

      {thread.length > 0 && (
        <ul className="msgthread">
          {thread.map((m) => (
            <li key={m.ulid}>
              <div
                className={`message message-origin-${m.origin ?? 'unknown'}`}
                data-origin={m.origin}
              >
                <div className="message-meta">
                  {t(
                    m.origin === 'denunciante_message'
                      ? 'mensagens.origem_denunciante'
                      : 'mensagens.origem_comite',
                  )}{' '}
                  {/* Mensagem do comitê traz data e hora; a do denunciante cai na
                      semana, porque hora fina do lado dele é o vetor do LT-01. */}
                  · {dataHora(m.createdAtEpochMs, m.createdWeek)}
                </div>
                {/* `pre-wrap` fica no CSS junto do resto do estilo da mensagem;
                    era a única propriedade visual que sobrava inline. */}
                <div className="message-texto">{m.text}</div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* ── ANEXOS DO CASO, BAIXÁVEIS (operador, 2026-08-13) ────────────────────
          Ficam no mesmo lugar do thread porque é onde o comitê procura o que o
          denunciante mandou — o pedido foi "tudo o que o usuário enviar no chat do
          denunciante", e o arquivo é parte disso.

          ⚠️ O NOME do arquivo não aparece, e não é omissão: ele nunca é persistido
          (o `AttachmentRef` guarda `{contentHash, nonce, sizeBucket}`), porque nome
          de arquivo identifica ("laudo-joao-silva.pdf"). O denunciante ANUNCIA os
          nomes na mensagem cifrada, logo acima — é de lá que se lê qual é qual.
          Mostrar um nome inventado aqui seria pior que mostrar o hash. */}
      {temAcesso && attachments !== undefined && attachments.length > 0 && (
        <div className="msganexos" data-testid="msg-anexos">
          <strong className="msganexos__titulo">
            {t('mensagens.anexos_titulo', { count: attachments.length })}
          </strong>
          <ul className="msganexos__lista">
            {attachments.map((a) => (
              <li key={a.contentHash}>
                <code className="msganexos__hash" title={a.contentHash}>
                  {a.contentHash.slice(0, 16)}
                </code>
                <span className="msganexos__bucket">{a.sizeBucket}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={baixando === a.contentHash}
                  onClick={() => {
                    void handleBaixar(a.contentHash);
                  }}
                >
                  {t('mensagens.anexo_baixar')}
                </Button>
                {erroAnexo !== null && erroAnexo.hash === a.contentHash && (
                  <span className="msganexos__erro" role="alert">
                    {t(`mensagens.anexo_erro_${erroAnexo.erro}`)}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {/* A decifra acontece AQUI, no browser — o servidor entrega bytes cifrados e
              não tem a DEK. A afirmação é literal, igual ao selo do denunciante. */}
          <p className="msganexos__nota">{t('mensagens.anexos_nota')}</p>
        </div>
      )}

      {!temAcesso ? (
        <Alert variant="info" role="status">
          <strong>{t('mensagens.indisponivel_title')}</strong>
          <div>{t('mensagens.indisponivel_body')}</div>
        </Alert>
      ) : (
        <div className="msgbox">
          {/* A lombada. `aria-hidden` porque é a MESMA regra que o parágrafo de
              cautela abaixo entrega como texto — anunciá-la duas vezes seria
              ruído no leitor de tela, e o rótulo vertical é a cópia decorativa,
              não a fonte. */}
          <div className="msgbox__rail" aria-hidden="true">
            <span className="msgbox__rail-label">{t('mensagens.canal_auditado')}</span>
          </div>

          <div className="msgbox__corpo">
            <label htmlFor="msg-composer" className="msgbox__label">
              {t('mensagens.aria_label')}
            </label>

            {/* Duas frases, dois papéis: a primeira é a consequência (entra no
                log), a segunda é a RESTRIÇÃO — e é a única coisa em destaque na
                caixa, porque é o que muda o que vai ser escrito. */}
            <p className="msgbox__cautela">
              {t('mensagens.cautela_registro')}{' '}
              <strong>{t('mensagens.cautela_nao_identificar')}</strong>
            </p>

            {/* Primitivo `<Textarea>` (`cf-input cf-textarea`), não `<textarea>`
                cru. Ele existia no @aions/ui-primitives com foco, disabled e
                placeholder já resolvidos por token, e não tinha UM consumidor em
                app nenhum — a cura estava no repo, desconectada. Escrever CSS
                próprio aqui criaria a segunda cópia da mesma defesa. */}
            <Textarea
              id="msg-composer"
              aria-label={t('mensagens.aria_label')}
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                if (sendState !== 'idle') setSendState('idle');
              }}
              rows={4}
              disabled={sendState === 'sending'}
            />
            {sendState === 'sent' && (
              <Alert variant="success" role="status" style={{ marginTop: 8 }}>
                {t('mensagens.enviada')}
              </Alert>
            )}
            {sendState === 'error' && (
              <Alert variant="error" role="alert" style={{ marginTop: 8 }}>
                {t('mensagens.erro')}
              </Alert>
            )}
            <div className="msgbox__acao">
              <Button
                variant="primary"
                loading={sendState === 'sending'}
                disabled={texto.trim() === '' || sendState === 'sending' || sessaoExpirada}
                onClick={() => void handleEnviar()}
              >
                {sendState === 'sending' ? t('mensagens.enviando') : t('mensagens.enviar')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
